import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";
import { CATEGORY_LABEL } from "@/lib/clientCategories";

function tone(days) {
  if (days <= 2) return { bar: "bg-rose-600", text: "text-rose-700", chip: "bg-rose-50 text-rose-700 border-rose-200" };
  if (days <= 7) return { bar: "bg-amber-500", text: "text-amber-700", chip: "bg-amber-50 text-amber-800 border-amber-200" };
  return { bar: "bg-zinc-900", text: "text-zinc-700", chip: "bg-zinc-100 text-zinc-700 border-zinc-200" };
}

function fmt(d) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

const HORIZONS = [45, 90, 180, 365];
const CAT_LINK = { gst_monthly: "/clients?category=gst_monthly", gst_quarterly: "/clients?category=gst_quarterly", itr: "/clients?category=itr", tds: "/clients?category=other" };

export default function FilingCalendar() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const [days, setDays] = useState(45);

  useEffect(() => {
    setOpenIdx(null);
    api.get("/filing-calendar", { params: { days } }).then(r => setData(r.data)).catch(() => setErr(true));
  }, [days]);

  if (err) return null;

  return (
    <section data-testid="panel-filing-calendar" className="bg-white border border-zinc-200 p-6">
      <header className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays size={16} strokeWidth={1.75} />
          <h2 className="font-heading font-bold tracking-tight text-lg">Filing calendar</h2>
          <div className="flex ml-2 border border-zinc-200" data-testid="fc-horizon">
            {HORIZONS.map(h => (
              <button key={h} data-testid={`fc-horizon-${h}`} onClick={() => setDays(h)}
                className={`text-[10px] font-semibold tabular px-2 py-1 transition-colors ${days === h ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>
                {h}d
              </button>
            ))}
          </div>
        </div>
        {data ? (
          <div className="flex flex-wrap gap-2 text-[11px]">
            {[["gst_monthly", "GST-M", "fc-count-monthly"], ["gst_quarterly", "GST-Q", "fc-count-quarterly"], ["itr", "ITR", "fc-count-itr"], ["tds", "TDS", "fc-count-tds"]].map(([k, l, tid]) => (
              <Link key={k} to={CAT_LINK[k]} data-testid={tid} className="px-2 py-1 border border-zinc-200 hover:bg-zinc-900 hover:text-white transition-colors">
                {l} <b className="tabular">{data.counts[k] ?? 0}</b>
              </Link>
            ))}
          </div>
        ) : null}
      </header>

      {!data ? <div className="text-sm text-zinc-500">Loading…</div> : !data.items.length ? (
        <div className="text-sm text-zinc-500" data-testid="fc-empty">No statutory due dates in this window.</div>
      ) : (
        <ul className="divide-y divide-zinc-100" data-testid="fc-list">
          {data.items.map((it, i) => {
            const t = tone(it.days_left);
            const open = openIdx === i;
            return (
              <li key={`${it.form}-${it.due_date}`} data-testid={`fc-item-${i}`}>
                <button
                  data-testid={`fc-toggle-${i}`}
                  onClick={() => setOpenIdx(open ? null : i)}
                  className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-zinc-50 -mx-2 px-2 transition-colors"
                >
                  <span className={`w-1 self-stretch ${t.bar}`} />
                  <div className="w-14 shrink-0">
                    <div className="font-heading font-bold text-sm tabular leading-none">{fmt(it.due_date)}</div>
                    <div className={`text-[10px] font-semibold tabular ${t.text}`}>
                      {it.days_left === 0 ? "Today" : it.days_left === 1 ? "Tomorrow" : `${it.days_left}d left`}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{it.form} <span className="text-zinc-400 font-normal">· {it.period}</span></div>
                    <div className="text-[11px] text-zinc-500 truncate">{it.desc}</div>
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${t.chip} whitespace-nowrap`}>
                    {it.client_count} {it.client_count === 1 ? "client" : "clients"}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 hidden sm:inline">{CATEGORY_LABEL[it.category]}</span>
                  {open ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
                </button>
                {open ? (
                  <div className="pl-[4.75rem] pb-3" data-testid={`fc-clients-${i}`}>
                    {it.clients.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {it.clients.map(c => (
                          <span key={c.id} className="text-[11px] px-2 py-1 bg-zinc-50 border border-zinc-200">
                            {c.name}{c.gstin ? <span className="text-zinc-400 tabular"> · {c.gstin}</span> : null}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-zinc-500">
                        No clients tagged {CATEGORY_LABEL[it.category]} yet. <Link to={CAT_LINK[it.category]} className="underline">Tag clients →</Link>
                      </div>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
